/**
 * AIS target detail — the tapped vessel's name, MMSI, type, course and speed, and
 * its CPA/TCPA against own ship right now. Opened when an AIS triangle is tapped.
 *
 * Ageing matters here: between "lost" (~6 min silent) and "drop" (~12 min) a
 * target is still in the store but is a ghost — its position and course are
 * stale guesses. So we show how long ago we last heard from it, flag and dim a
 * lost contact, and DON'T present a confident CPA on one, the same way the plot
 * and the alarms distrust it.
 */
import { useChartPlotterStore } from '@/store';
import { useAisStore } from '@/store/ais';
import { computeCpa } from '@/ais/cpa';
import { classifyTarget, targetAgeMs } from '@/ais/targetAge';
import { formatBearing } from '@/nav';
import { Sheet } from './Sheet';

export function AisSheet() {
  const selected = useAisStore((s) => s.selectedMmsi);
  const targets = useAisStore((s) => s.targets);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const t = selected != null ? targets[selected] : undefined;

  const nowMs = Date.now();
  const status = t ? classifyTarget(t, nowMs) : 'live';
  const lost = status === 'lost';
  const ageMs = t ? targetAgeMs(t.lastMs, nowMs) : 0;
  const heardMin = Math.round(ageMs / 60_000);
  const heardText = ageMs < 60_000 ? 'just now' : `${heardMin} min ago`;

  // A lost target's position and course are stale guesses, so a CPA off them
  // would read as confident when it isn't. Don't compute one; say why instead.
  const cpa =
    t && ownship.position && !lost
      ? computeCpa(
          { position: ownship.position, cogDeg: ownship.cog || ownship.heading, sogKn: ownship.sog },
          { position: t.position, cogDeg: t.cogDeg, sogKn: t.sogKn },
        )
      : null;

  return (
    <Sheet name="ais" title="AIS target" sub="The tapped vessel and its closest point of approach to us now.">
      {!t ? (
        <p className="acp-note">No target selected, or it’s no longer being received.</p>
      ) : (
        <div style={{ opacity: lost ? 0.6 : 1 }}>
          <div className="acp-kv">
            <span className="k">Name</span>
            <span className="v">
              {t.name || '—'}
              {lost ? ' · lost' : status === 'stale' ? ' · stale' : ''}
            </span>
          </div>
          <div className="acp-kv">
            <span className="k">MMSI</span>
            <span className="v">{t.mmsi}</span>
          </div>
          <div className="acp-kv">
            <span className="k">Type</span>
            <span className="v">{t.shipType || '—'}</span>
          </div>
          {t.lengthM ? (
            <div className="acp-kv">
              <span className="k">Length</span>
              <span className="v">{t.lengthM} m</span>
            </div>
          ) : null}
          <div className="acp-kv">
            <span className="k">Course / Speed</span>
            <span className="v">
              {formatBearing(t.cogDeg)} · {t.sogKn.toFixed(1)} kn
            </span>
          </div>
          <div className="acp-kv">
            <span className="k">Last heard</span>
            <span className="v">{heardText}</span>
          </div>
          {lost ? (
            <p className="acp-note">
              Not heard from in {heardMin} min, so this contact is treated as lost. Its position,
              course and any CPA are stale guesses now — we don’t show a CPA until it reports again.
            </p>
          ) : cpa ? (
            <>
              <div className="acp-kv">
                <span className="k">Range now</span>
                <span className="v">{cpa.rangeNm.toFixed(2)} nm</span>
              </div>
              <div className="acp-kv">
                <span className="k">CPA</span>
                <span className="v">{cpa.cpaNm.toFixed(2)} nm</span>
              </div>
              <div className="acp-kv">
                <span className="k">TCPA</span>
                <span className="v">
                  {cpa.tcpaMin > 0 ? `${cpa.tcpaMin.toFixed(0)} min` : 'past / opening'}
                </span>
              </div>
            </>
          ) : (
            <p className="acp-note">No own-ship fix, so CPA/TCPA can’t be worked out.</p>
          )}
        </div>
      )}
    </Sheet>
  );
}
